import { useIsMutating, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { DashboardQueryKey } from '../constants/dashboardQueryKeys';
import { systemService } from '../services/system/SystemService';
import { MigrationOptions, SystemRun, SystemRunKind } from '../types/system';

const systemKeys = {
  migrations: () => [DashboardQueryKey.SYSTEM_MIGRATIONS] as const,
  runs: (kind?: SystemRunKind) => [DashboardQueryKey.SYSTEM_RUNS, kind ?? 'all'] as const,
  run: (id: string) => [DashboardQueryKey.SYSTEM_RUNS, 'one', id] as const,
  backups: () => [DashboardQueryKey.SYSTEM_BACKUPS] as const,
  database: () => [DashboardQueryKey.SYSTEM_DATABASE] as const,
};

/**
 * Shared by every action that writes (run, revert, backup, restore). There is
 * no server-side lock: while any of them is in flight the page disables all
 * of its action buttons.
 */
export const SYSTEM_ACTION_KEY = ['systemAction'] as const;

/** A run still `running` after this long was left by a stopped server. */
const STALE_RUN_MS = 60 * 60 * 1000;
const RUNNING_POLL_MS = 10 * 1000;

const isLiveRun = (r: SystemRun) =>
  r.status === 'running' && Date.now() - new Date(r.startedAt).getTime() < STALE_RUN_MS;

/**
 * True while this tab has an action in flight, or the history shows one
 * running — e.g. after a reload in the middle of a restore. Polls the
 * history until that run finishes.
 */
export function useSystemBusy(): boolean {
  const inFlight = useIsMutating({ mutationKey: SYSTEM_ACTION_KEY }) > 0;
  const runs = useQuery({
    queryKey: systemKeys.runs(),
    queryFn: () => systemService.listRuns(),
    refetchInterval: (query) =>
      (query.state.data ?? []).some(isLiveRun) ? RUNNING_POLL_MS : false,
  });
  return inFlight || (runs.data ?? []).some(isLiveRun);
}

export function useMigrationsQuery() {
  return useQuery({
    queryKey: systemKeys.migrations(),
    queryFn: () => systemService.listMigrations(),
  });
}

export function useSystemRunsQuery(kind?: SystemRunKind) {
  return useQuery({ queryKey: systemKeys.runs(kind), queryFn: () => systemService.listRuns(kind) });
}

export function useSystemRunQuery(id: string | null) {
  return useQuery({
    queryKey: systemKeys.run(id ?? ''),
    queryFn: () => systemService.getRun(id as string),
    enabled: Boolean(id),
  });
}

export function useBackupsQuery() {
  return useQuery({ queryKey: systemKeys.backups(), queryFn: () => systemService.listBackups() });
}

export function useDatabaseNameQuery(enabled: boolean) {
  return useQuery({
    queryKey: systemKeys.database(),
    queryFn: () => systemService.databaseName(),
    enabled,
    staleTime: Infinity,
  });
}

/** After any action the run history and the backup list may have changed. */
function useInvalidateSystem() {
  const qc = useQueryClient();
  return () =>
    Promise.all([
      qc.invalidateQueries({ queryKey: [DashboardQueryKey.SYSTEM_RUNS] }),
      qc.invalidateQueries({ queryKey: systemKeys.backups() }),
    ]);
}

export function usePreviewMigrationMutation() {
  return useMutation({
    mutationFn: ({ id, options }: { id: string; options: MigrationOptions }) =>
      systemService.previewMigration(id, options),
  });
}

// onSettled, not onSuccess: a failed action is still a new history row.

export function useRunMigrationMutation() {
  const invalidate = useInvalidateSystem();
  return useMutation({
    mutationKey: SYSTEM_ACTION_KEY,
    mutationFn: ({ id, options }: { id: string; options: MigrationOptions }) =>
      systemService.runMigration(id, options),
    onSettled: invalidate,
  });
}

export function useRevertRunMutation() {
  const invalidate = useInvalidateSystem();
  return useMutation({
    mutationKey: SYSTEM_ACTION_KEY,
    mutationFn: (id: string) => systemService.revertRun(id),
    onSettled: invalidate,
  });
}

export function useCreateBackupMutation() {
  const invalidate = useInvalidateSystem();
  return useMutation({
    mutationKey: SYSTEM_ACTION_KEY,
    mutationFn: () => systemService.createBackup(),
    onSettled: invalidate,
  });
}

export function useRestoreBackupMutation() {
  const invalidate = useInvalidateSystem();
  return useMutation({
    mutationKey: SYSTEM_ACTION_KEY,
    mutationFn: ({ fileName, confirmDatabase }: { fileName: string; confirmDatabase: string }) =>
      systemService.restoreBackup(fileName, confirmDatabase),
    onSettled: invalidate,
  });
}
